import { Component, Inject, OnInit, ViewChildren, QueryList, ElementRef, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-totp-setup-modal',
  templateUrl: './totp-setup-modal.component.html',
  styleUrls: ['./totp-setup-modal.component.scss']
})
export class TotpSetupModalComponent implements OnInit, AfterViewInit {
  @ViewChildren('totpInput') totpInputs!: QueryList<ElementRef<HTMLInputElement>>;
  verificationCode: string[] = ['', '', '', '', '', ''];
  errorMessage: string = '';
  isLoading = false;

  // Data passed to the modal: temporary token, secret, and QR code URL
  constructor(
    public dialogRef: MatDialogRef<TotpSetupModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { token: string; secret: string; totpUrl: string },
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    // The QR code and secret are provided via `data` input.
    // No specific initialization logic needed here beyond displaying them.
  }

  ngAfterViewInit(): void {
    if (this.totpInputs && this.totpInputs.first) {
      this.totpInputs.first.nativeElement.focus();
    }
  }

  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    if (value.length > 1) {
      value = value.charAt(0);
      input.value = value;
    }
    this.verificationCode[index] = value;
    if (value && index < this.totpInputs.length - 1) {
      this.totpInputs.toArray()[index + 1].nativeElement.focus();
    }
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && this.verificationCode[index] === '' && index > 0) {
      this.totpInputs.toArray()[index - 1].nativeElement.focus();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.onVerifyAndCompleteSetup();
    }
  }

  onOtpBackspace(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && this.verificationCode[index] === '' && index > 0) {
      this.totpInputs.toArray()[index - 1].nativeElement.focus();
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '');
    if (digits.length === 6) {
      const inputArray = this.totpInputs.toArray();
      for (let i = 0; i < 6; i++) {
        this.verificationCode[i] = digits[i];
        inputArray[i].nativeElement.value = digits[i];
      }
      inputArray[5].nativeElement.focus();
      event.preventDefault();
    }
  }

  /**
   * Handles the verification and completion of TOTP setup.
   * Calls the AuthService to verify the provided code and finalize setup.
   */
  async onVerifyAndCompleteSetup(): Promise<void> {
    const code = this.verificationCode.join('');
    if (code.length !== 6 || !/^\d+$/.test(code)) {
      this.errorMessage = 'Please enter a valid 6-digit code.';
      return;
    }

    this.errorMessage = ''; // Clear previous errors
    this.isLoading = true; // Show loading spinner

    try {
      const response = await this.authService.setupTotp(this.data.token, code).toPromise();

      if (response?.error) {
        this.errorMessage = response.error;
      } else if (response?.token) {
        // TOTP setup successful, AuthService already handles redirect
        this.dialogRef.close(true); // Close modal and indicate success
      } else {
        this.errorMessage = 'Setup failed. Please try again.';
      }
    } catch (error) {
      this.errorMessage = 'Setup failed. Please try again.';
      console.error('TOTP setup error:', error);
    } finally {
      this.isLoading = false; // Hide loading spinner
    }
  }

  /**
   * Handles the cancel action.
   * Closes the modal without indicating success.
   */
  onCancel(): void {
    this.dialogRef.close(false); // Close modal and indicate cancellation/failure
  }

  get isVerificationCodeIncomplete(): boolean {
    return this.verificationCode.some(char => char === '');
  }
}
